import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

/**
 * A loan-scoped list the server narrows to one person -- the shape both the
 * income formula engine's data endpoints share (`extracted-values`,
 * `los-values`).
 */
export interface LoanBorrowerScopedParams {
  loanId: string;
  /** Narrows the response to one person's data. Sent together; without them
   *  the server returns everything on the loan, which is what a stream with
   *  no borrower pair needs. */
  borrowerPairId?: string | null;
  scope?: string | null;
}

/**
 * Fetches `/loans/{loanId}/{resource}` with that narrowing, returning the
 * response's `data` array.
 *
 * Shared so the two endpoints cannot drift on the part that matters: `scope`
 * only goes up WITH a borrowerPairId, since the server ignores it alone.
 */
export async function fetchBorrowerScopedList<T>(
  resource: string,
  params: LoanBorrowerScopedParams,
  /** Further query params one endpoint takes; empty values are not sent. */
  extra: Record<string, string | null | undefined> = {},
): Promise<T[]> {
  const search = new URLSearchParams();
  if (params.borrowerPairId) {
    search.set('borrowerPairId', params.borrowerPairId);
    if (params.scope) search.set('scope', params.scope);
  }
  for (const [key, value] of Object.entries(extra)) {
    if (value) search.set(key, value);
  }
  const query = search.toString();

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<{ success: boolean; data: T[] }>(
    `/loans/${params.loanId}/${resource}${query ? `?${query}` : ''}`,
    { method: 'GET' },
  );

  return response.data?.data ?? [];
}
